import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import api from '../../../api/axios';

const CARD_CONFIG = [
  {
    id: 'total_ndr',
    label: 'Total NDR Orders',
    icon: 'Package',
    iconBg: 'bg-blue-50',
    iconColor: '#3b82f6',
    sub: 'vs last period',
    valueKey: 'totalNdr',
    prevKey: 'prevTotalNdr',
    isPercent: false,
  },
  {
    id: 'rescheduled',
    label: '% Successfully Rescheduled',
    icon: 'CalendarCheck',
    iconBg: 'bg-green-50',
    iconColor: '#22c55e',
    sub: 'vs last period',
    valueKey: 'rescheduledPercent',
    volumeKey: 'rescheduledCount',
    prevKey: 'prevRescheduledPercent',
    isPercent: true,
  },
  {
    id: 'avg_calls',
    label: 'Avg Calls Attempted',
    icon: 'PhoneCall',
    iconBg: 'bg-amber-50',
    iconColor: '#f59e0b',
    sub: 'per NDR order',
    valueKey: 'avgCallsAttempted',
    prevKey: 'prevAvgCallsAttempted',
    isPercent: false,
    isDecimal: true,
  },
  {
    id: 'delivered_after',
    label: 'Delivered After NDR',
    icon: 'CheckCircle2',
    iconBg: 'bg-emerald-50',
    iconColor: '#10b981',
    sub: 'orders delivered',
    valueKey: 'deliveredAfterNdrPercent',
    volumeKey: 'deliveredAfterNdr',
    prevKey: 'prevDeliveredAfterNdrPercent',
    isPercent: true,
  },
  {
    id: 'rto',
    label: 'RTO %',
    icon: 'RotateCcw',
    iconBg: 'bg-red-50',
    iconColor: '#ef4444',
    sub: 'return to origin',
    valueKey: 'rtoPercent',
    volumeKey: 'rtoCount',
    prevKey: 'prevRtoPercent',
    isPercent: true,
    invertTrend: true,
  },
];

const NDRMetricCards = ({ from, to, refreshKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get('/api/v1/ndr-management/metrics', { params: { from, to } })
      .then((res) => {
        if (!cancelled) setData(res.data?.data || {});
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [from, to, refreshKey]);

  const getValue = (card) => {
    if (!data) return { value: '—', volume: null };
    const val = data[card.valueKey];
    if (val == null) return { value: '—', volume: null };
    if (card.isPercent) {
      const volume = card.volumeKey ? (data[card.volumeKey] || 0).toLocaleString() : null;
      return { value: `${val}%`, volume };
    }
    if (card.isDecimal) return { value: val.toFixed(1), volume: null };
    return { value: val.toLocaleString(), volume: null };
  };

  const getTrend = (card) => {
    if (!data) return { text: '', up: true };
    const curr = data[card.valueKey] ?? 0;
    const prev = data[card.prevKey] ?? 0;
    if (card.isPercent || card.isDecimal) {
      const diff = curr - prev;
      const sign = diff >= 0 ? '+' : '';
      const trendUp = card.invertTrend ? diff <= 0 : diff >= 0;
      return { text: `${sign}${diff.toFixed(1)}${card.isPercent ? '%' : ''}`, up: trendUp };
    }
    if (prev === 0) return { text: curr > 0 ? '+100%' : '0%', up: curr >= 0 };
    const pctChange = ((curr - prev) / prev) * 100;
    const sign = pctChange >= 0 ? '+' : '';
    const trendUp = card.invertTrend ? pctChange <= 0 : pctChange >= 0;
    return { text: `${sign}${pctChange.toFixed(1)}%`, up: trendUp };
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
      {CARD_CONFIG.map((m) => {
        const trend = getTrend(m);
        const { value, volume } = getValue(m);
        return (
          <div
            key={m.id}
            className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col gap-3 hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex items-center justify-between">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${m.iconBg}`}>
                <Icon name={m.icon} size={18} color={m.iconColor} />
              </div>
              {!loading && data && (
                <span
                  className={`flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full ${
                    trend.up ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'
                  }`}
                >
                  <Icon name={trend.up ? 'TrendingUp' : 'TrendingDown'} size={11} />
                  {trend.text}
                </span>
              )}
            </div>
            <div>
              {loading ? (
                <div className="h-8 w-20 bg-gray-100 rounded animate-pulse" />
              ) : (
                <>
                  <p className="text-xl md:text-2xl font-bold text-gray-900 leading-tight">{value}</p>
                  {volume && (
                    <p className="text-sm font-semibold text-gray-500 leading-tight">{volume} orders</p>
                  )}
                </>
              )}
              <p className="text-xs font-medium text-gray-500 mt-0.5">{m.label}</p>
              <p className="text-[11px] text-gray-400 mt-0.5">{m.sub}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default NDRMetricCards;
